/** Institution admin (STORY-021): members with roles and state, invites that provision teacher
 * accounts, the institution's AI policy (STORY-013) and its usage (STORY-100). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCloudOrigin } from "../platform/joinOrigin";
import { TeacherChrome } from "../teacher/Chrome";
import { InstitutionUsage } from "../teacher/InstitutionUsage";
import { adminFlags, createInvite, deactivateMember, listInstitutions, listInvites, membersDetail, myReach, reactivateMember, setFlag, setInstitutionSettings, setMemberRole, type InstitutionPolicy, type Invite } from "../teacher/api";
import { Button } from "../ui";

function InstitutionPanel({ id, name, policy }: { id: string; name: string; policy: InstitutionPolicy }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const members = useQuery({ queryKey: ["members", id], queryFn: () => membersDetail(id) });
  const invites = useQuery({ queryKey: ["invites", id], queryFn: () => listInvites(id) });
  const [email, setEmail] = useState("");
  const [dname, setDname] = useState("");
  const [role, setRole] = useState("teacher");
  const [last, setLast] = useState<Invite | null>(null);
  // An invite is opened by someone outside this room, so it must name the server, never the
  // 127.0.0.1 the Teacher Computer app serves its own windows from.
  const inviteOrigin = useCloudOrigin();
  const [pol, setPol] = useState<InstitutionPolicy>(policy);
  const [saved, setSaved] = useState(false);
  const setDefault = (key: keyof InstitutionPolicy["defaults"], value: string | boolean | null) => {
    setSaved(false);
    setPol((p) => ({ ...p, defaults: { ...p.defaults, [key]: value === "" ? null : value } }));
  };
  const toggleLock = (key: string) => {
    setSaved(false);
    setPol((p) => ({ ...p, locked: p.locked.includes(key) ? p.locked.filter((k) => k !== key) : [...p.locked, key] }));
  };
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["members", id] });
    void qc.invalidateQueries({ queryKey: ["invites", id] });
  };
  const invite = useMutation({
    mutationFn: () => createInvite(id, { email: email.trim(), role, display_name: dname.trim() || undefined }),
    onSuccess: (inv) => { setLast(inv); setEmail(""); setDname(""); refresh(); },
  });
  const roleChange = useMutation({ mutationFn: ({ sub, r }: { sub: string; r: string }) => setMemberRole(id, sub, r), onSuccess: refresh });
  const toggle = useMutation({ mutationFn: ({ sub, active }: { sub: string; active: boolean }) => (active ? deactivateMember(id, sub) : reactivateMember(id, sub)), onSuccess: refresh });
  const settings = useMutation({ mutationFn: () => setInstitutionSettings(id, pol), onSuccess: () => setSaved(true) });
  const fmt = (iso: string) => new Date(iso).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  const card = "rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";
  return (
    <section className="flex flex-col gap-4">
      <h2 className="display text-xl">{name} <span className="font-mono text-xs text-[color:var(--ink-2)]">{id}</span></h2>
      <div className={card}>
        <h3 className="mb-2 font-semibold">{t("inst.members")}</h3>
        <ul className="flex flex-col gap-2 text-sm">
          {members.data?.map((m) => (
            <li key={m.sub} className={`flex flex-wrap items-center gap-2 ${m.active ? "" : "opacity-60"}`}>
              <span className="min-w-0 flex-1 truncate">{m.display_name || m.email || m.sub}{m.email && m.display_name ? ` · ${m.email}` : ""}</span>
              <select className="field field-sm" value={m.role} aria-label={t("inst.role")} onChange={(e) => roleChange.mutate({ sub: m.sub, r: e.target.value })} disabled={!m.active}>
                <option value="teacher">{t("inst.roles.teacher")}</option>
                <option value="admin">{t("inst.roles.admin")}</option>
              </select>
              {!m.active && m.deactivated_at && <span className="text-xs text-[color:var(--ink-2)]">{t("inst.inactive", { at: fmt(m.deactivated_at) })}</span>}
              <Button variant={m.active ? "destructive" : "secondary"} size="sm" loading={toggle.isPending && toggle.variables?.sub === m.sub} onClick={() => toggle.mutate({ sub: m.sub, active: m.active })}>{m.active ? t("inst.deactivate") : t("inst.reactivate")}</Button>
            </li>
          ))}
        </ul>
      </div>
      {/* The 'personal' sentinel is where a teacher's own courses live, not an institution;
          the analytics service refuses it by contract, so it gets the sentence, not a query
          that ends in "could not load". */}
      {id === "personal" ? <p className={`${card} text-sm text-[color:var(--ink-2)]`}>{t("usage.personal")}</p> : <InstitutionUsage id={id} />}
      <form className={`${card} flex flex-wrap items-end gap-2`} onSubmit={(e) => { e.preventDefault(); invite.mutate(); }}>
        <h3 className="w-full font-semibold">{t("inst.invite")}</h3>
        <label className="flex flex-col gap-1 text-sm">{t("inst.email")}<input className="field" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <label className="flex flex-col gap-1 text-sm">{t("inst.name")}<input className="field" value={dname} onChange={(e) => setDname(e.target.value)} /></label>
        <label className="flex flex-col gap-1 text-sm">{t("inst.role")}
          <select className="field" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="teacher">{t("inst.roles.teacher")}</option>
            <option value="admin">{t("inst.roles.admin")}</option>
          </select>
        </label>
        <Button type="submit" loading={invite.isPending} disabled={!email.trim()}>{t("inst.send")}</Button>
        {last && (
          <div className="w-full rounded-[var(--radius-control)] bg-[color:var(--surface-2)] p-3 text-sm" role="status">
            <p className="mb-1">{t("inst.created")}</p>
            {inviteOrigin && <p><span className="text-[color:var(--ink-2)]">{t("inst.link")}:</span> <code className="break-all">{`${inviteOrigin}${last.link}`}</code></p>}
            {last.temp_password && <p><span className="text-[color:var(--ink-2)]">{t("inst.tempPassword")}:</span> <code>{last.temp_password}</code></p>}
            {!last.provisioned && <p className="text-xs text-[color:var(--ink-2)]">{t("inst.notProvisioned")}</p>}
          </div>
        )}
      </form>
      {invites.data && invites.data.length > 0 && (
        <div className={card}>
          <h3 className="mb-2 font-semibold">{t("inst.invites")}</h3>
          <ul className="flex flex-col gap-1 text-sm">
            {invites.data.map((i) => (
              <li key={i.token} className="flex flex-wrap gap-2">
                <span className="min-w-0 flex-1 truncate">{i.email}{i.display_name ? ` · ${i.display_name}` : ""} · {t(`inst.roles.${i.role}`)}</span>
                <span className="text-xs text-[color:var(--ink-2)]">{i.accepted_at ? t("inst.accepted") : `${t("inst.pending")} · ${t("inst.expires", { at: fmt(i.expires_at) })}`}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className={`${card} flex flex-col gap-3 text-sm`}>
        <h3 className="font-semibold">{t("inst.policy")}</h3>
        <p className="text-xs text-[color:var(--ink-2)]">{t("inst.policyHint")}</p>
        <label className="flex flex-wrap items-center gap-2">
          {t("inst.settings")}
          <select className="field" value={pol.ai_route} onChange={(e) => { setSaved(false); setPol((p) => ({ ...p, ai_route: e.target.value })); }}>
            <option value="local">{t("inst.aiLocal")}</option>
            <option value="cloud">{t("inst.aiCloud")}</option>
          </select>
        </label>
        {([
          ["join_mode", ["", "name", "seat", "anonymous"], "course.joinModes"],
          ["privacy_mode", ["", "standard", "guest_only"], "inst.privacy"],
          ["report_release", ["", "per_session", "automatic"], "inst.release"],
        ] as const).map(([key, values, ns]) => (
          <div key={key} className="flex flex-wrap items-center gap-2">
            <span className="w-40">{t(`inst.keys.${key}`)}</span>
            <select className="field" value={String(pol.defaults[key] ?? "")} onChange={(e) => setDefault(key, e.target.value)}>
              {values.map((v) => <option key={v} value={v}>{v === "" ? t("inst.noDefault") : t(`${ns}.${v}`)}</option>)}
            </select>
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={pol.locked.includes(key)} disabled={!pol.defaults[key]} onChange={() => toggleLock(key)} />{t("inst.lock")}</label>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2">
          <span className="w-40">{t("inst.keys.attention_flags")}</span>
          <select className="field" value={pol.defaults.attention_flags == null ? "" : String(pol.defaults.attention_flags)} onChange={(e) => setDefault("attention_flags", e.target.value === "" ? null : e.target.value === "true")}>
            <option value="">{t("inst.noDefault")}</option>
            <option value="true">{t("inst.on")}</option>
            <option value="false">{t("inst.off")}</option>
          </select>
          <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={pol.locked.includes("attention_flags")} disabled={pol.defaults.attention_flags == null} onChange={() => toggleLock("attention_flags")} />{t("inst.lock")}</label>
        </div>
        <label className="flex flex-wrap items-center gap-2">
          {t("inst.retention")}
          <input className="field w-24" type="number" min={30} max={3650} value={pol.retention_days ?? ""} onChange={(e) => { setSaved(false); setPol((p) => ({ ...p, retention_days: e.target.value ? Number(e.target.value) : null })); }} />
        </label>
        <div className="flex items-center gap-2">
          <Button variant="secondary" loading={settings.isPending} onClick={() => settings.mutate()}>{t("inst.save")}</Button>
          {saved && <span className="text-[color:var(--secure)]">{t("inst.saved")}</span>}
          {settings.error && <span style={{ color: "var(--danger)" }}>{t("inst.policyFailed")}</span>}
        </div>
      </div>
    </section>
  );
}

function FlagsPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const flags = useQuery({ queryKey: ["admin-flags"], queryFn: adminFlags, retry: false });
  const [key, setKey] = useState("");
  const [scope, setScope] = useState("");
  const save = useMutation({
    mutationFn: ({ k, enabled, institutions }: { k: string; enabled: boolean; institutions: string[] }) => setFlag(k, { enabled, institutions }),
    onSuccess: () => { setKey(""); setScope(""); void qc.invalidateQueries({ queryKey: ["admin-flags"] }); void qc.invalidateQueries({ queryKey: ["flags"] }); },
  });
  const card = "rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";
  if (flags.isError) return null;
  return (
    <section className={`${card} flex flex-col gap-2 text-sm`}>
      <h2 className="display text-xl">{t("inst.flags")}</h2>
      <p className="text-xs text-[color:var(--ink-2)]">{t("inst.flagsHint")}</p>
      <ul className="flex flex-col gap-1">
        {flags.data?.map((f) => (
          <li key={f.key} className="flex flex-wrap items-center gap-2">
            <code className="min-w-32">{f.key}</code>
            <span className="text-xs text-[color:var(--ink-2)]">{f.institutions.length ? f.institutions.join(", ") : t("inst.everywhere")}{f.note ? ` · ${f.note}` : ""}</span>
            <Button variant={f.enabled ? "secondary" : "primary"} loading={save.isPending && save.variables?.k === f.key} onClick={() => save.mutate({ k: f.key, enabled: !f.enabled, institutions: f.institutions })}>{f.enabled ? t("inst.off") : t("inst.on")}</Button>
          </li>
        ))}
      </ul>
      <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); save.mutate({ k: key.trim(), enabled: true, institutions: scope.split(",").map((s) => s.trim()).filter(Boolean) }); }}>
        <label className="flex flex-col gap-1">{t("inst.flagKey")}<input className="field" value={key} onChange={(e) => setKey(e.target.value)} pattern="[a-z][a-z0-9_]{1,40}" /></label>
        <label className="flex flex-col gap-1">{t("inst.flagScope")}<input className="field" value={scope} onChange={(e) => setScope(e.target.value)} placeholder={t("inst.everywhere")} /></label>
        <Button type="submit" variant="secondary" disabled={!key.trim()}>{t("inst.flagAdd")}</Button>
      </form>
    </section>
  );
}

export function Institutions() {
  const { t } = useTranslation();
  const reach = useQuery({ queryKey: ["reach"], queryFn: myReach });
  const institutions = useQuery({ queryKey: ["institutions"], queryFn: listInstitutions });
  const mine = (institutions.data ?? []).filter((i) => reach.data?.global_admin || reach.data?.institutions.some((r) => r.institution_id === i.institution_id && r.role === "admin"));
  return (
    <TeacherChrome title={t("inst.title")} wide>
      <header className="-mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
        <Link to="/t" className="underline">{t("inst.toCourses")}</Link>
        <p className="measure text-[color:var(--ink-2)]">{t("inst.lede")}</p>
      </header>
      {reach.data && mine.length === 0 && <p className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-sm shadow-sm">{t("inst.none")}</p>}
      {mine.map((i) => {
        const s = i.settings as Partial<InstitutionPolicy>;
        return <InstitutionPanel key={i.institution_id} id={i.institution_id} name={i.name} policy={{ ai_route: s.ai_route ?? "local", defaults: s.defaults ?? {}, locked: s.locked ?? [], retention_days: s.retention_days ?? null }} />;
      })}
      {reach.data?.global_admin && <FlagsPanel />}
    </TeacherChrome>
  );
}
